<script lang="ts" setup>
import { useSlots, ref, computed } from 'vue'
import { useLocale } from '@composables/useLocale'
import { APP_VERSION } from '@shared/appVersion'
import { statusService } from '@lib/services/backend/status/status.service'
import BtnBack from './actions/BtnBack.vue'

const props = withDefaults(
  defineProps<{
    elevated?: boolean
  }>(),
  {
    elevated: false
  }
)

const { t } = useLocale()
const slots = useSlots()

const backVersion = ref<string>()
const versionVisible = ref(false)

// "vBack & vFront" when both are known; just "vFront" while back is loading or if it failed.
const versionsLabel = computed(() =>
  [backVersion.value, APP_VERSION]
    .filter(Boolean)
    .map((v) => `v${v}`)
    .join(' & ')
)

// Every toggle bumps the sequence: a fetch only writes its result if no later toggle
// happened meanwhile, so a slow stale response can't overwrite a newer one (or reappear
// after the versions were closed and reopened).
let versionRequestSeq = 0

// The back version is fetched every time the versions are revealed (not cached) because
// back can be redeployed during the same front session.
const toggleVersions = async () => {
  versionVisible.value = !versionVisible.value
  const seq = ++versionRequestSeq
  if (!versionVisible.value) return

  backVersion.value = undefined
  try {
    const { version } = await statusService.fetchBackVersion()
    if (seq === versionRequestSeq) backVersion.value = version
  } catch {
    // backVersion is already undefined (reset above) -> only vFront is shown.
    // Intentionally not assigning here: a stale failure must not clear a newer success.
  }
}
</script>

<template>
  <slot></slot>
  <section
    data-test="footer"
    class="gap-vw-16 flex w-full flex-row"
    :class="{ 'shadow-elevation-up': props.elevated }"
  >
    <!-- Identification user container -->
    <slot name="identification"></slot>

    <div class="mr-vw-96 gap-vh-16 flex flex-1 flex-col justify-end">
      <!-- Primary button section -->
      <slot name="primary-actions"></slot>

      <!-- Secondary button section (slot added or by default) -->
      <slot v-if="slots['secondary-actions']" name="secondary-actions"></slot>
      <div v-else class="gap-vw-16 flex flex-row items-center justify-end">
        <div class="w-1/2"></div>

        <BtnBack />
      </div>
      <!-- disclaimer -->
      <p
        data-test="footer-disclaimer"
        class="text-xsmall mb-vh-16 relative w-fit self-end"
        @click="toggleVersions"
      >
        {{ t('imagenes_de_caracter_ilustrativo') }}
        <b
          v-if="versionVisible"
          data-test="app-versions"
          class="top-vh-16 absolute right-0 whitespace-nowrap"
        >
          {{ versionsLabel }}
        </b>
      </p>
    </div>
  </section>
</template>
